package com.eladfinish.bible.contest.screens.browse.contentOfChapter.ui

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.contextmenu.provider.LocalTextContextMenuDropdownProvider
import androidx.compose.foundation.text.contextmenu.provider.LocalTextContextMenuToolbarProvider
import androidx.compose.foundation.text.contextmenu.provider.TextContextMenuDataProvider
import androidx.compose.foundation.text.contextmenu.provider.TextContextMenuProvider
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.ui.common.toFontResource
import com.eladfinish.bible.contest.ui.components.mouseVerticalScrollbar
import com.eladfinish.bible.contest.ui.extensions.navigationBarInsetPadding
import com.eladfinish.bible.contest.ui.layout.readableWidth
import dev.icerock.moko.resources.compose.fontFamilyResource
import kotlinx.coroutines.flow.collectLatest

/** Around the text inside the scrolling area: a scroll position is this much below the same spot in the text. */
internal val ChapterTextPadding = 16.dp

@Composable
internal fun ChapterText(
    annotated: AnnotatedString,
    scrollState: ScrollState,
    textAlign: TextAlign?,
    textSize: Float,
    stamFont: String?,
    onTextLayout: (TextLayoutResult) -> Unit,
    onCopySelected: (selectedText: String) -> Unit,
    onSearchSelected: (query: String) -> Unit,
    useCustomSelection: Boolean,
    contentPaddingDp: Float = 16f,
) {
    if (useCustomSelection) {
        // TODO: goToAliyah doesn't work here
        ChapterTextCustomSelection(
            annotated = annotated,
            scrollState = scrollState,
            textAlign = textAlign,
            textSize = textSize,
            stamFont = stamFont,
            onTextLayout = onTextLayout,
            onCopySelected = onCopySelected,
            onSearchSelected = onSearchSelected,
            contentPaddingDp = contentPaddingDp,
        )
    } else {
        ChapterTextSystemSelection(
            annotated = annotated,
            scrollState = scrollState,
            textAlign = textAlign,
            textSize = textSize,
            stamFont = stamFont,
            onTextLayout = onTextLayout,
        )
    }
}

@Composable
private fun ChapterTextSystemSelection(
    annotated: AnnotatedString,
    scrollState: ScrollState,
    textAlign: TextAlign?,
    textSize: Float,
    stamFont: String?,
    onTextLayout: (TextLayoutResult) -> Unit,
) {
    val baseStyle = LocalTextStyle.current
    val textStyle = if (stamFont != null) {
        val fontResource = stamFont.toFontResource()
        val fontFamily = fontFamilyResource(fontResource)
        baseStyle.copy(fontFamily = fontFamily)
    } else {
        baseStyle
    }

    SelectionContainer {
        Text(
            text = annotated,
            modifier = Modifier
                .fillMaxSize()
                .mouseVerticalScrollbar(scrollState)
                .verticalScroll(scrollState)
                .navigationBarInsetPadding()
                .readableWidth(max = 840.dp)
                .padding(ChapterTextPadding),
            color = Color.Black,
            textAlign = textAlign,
            fontSize = textSize.sp,
            style = textStyle,
            onTextLayout = onTextLayout,
        )
    }
}

@Composable
private fun ChapterTextCustomSelection(
    annotated: AnnotatedString,
    scrollState: ScrollState,
    textAlign: TextAlign?,
    textSize: Float,
    stamFont: String?,
    onTextLayout: (TextLayoutResult) -> Unit,
    onCopySelected: (selectedText: String) -> Unit,
    onSearchSelected: (query: String) -> Unit,
    contentPaddingDp: Float = 16f,
) {
    val baseStyle = LocalTextStyle.current
    val density = LocalDensity.current
    val contentPadding = contentPaddingDp.dp
    val contentPaddingPx = with(density) { contentPadding.toPx() }

    // Composable call must be outside remember { }.
    val fontFamily: FontFamily? = stamFont?.let { fontName ->
        val fontResource = fontName.toFontResource()
        fontFamilyResource(fontResource)
    }

    val textStyle: TextStyle = remember(baseStyle, fontFamily, textAlign, textSize) {
        var s = baseStyle
        if (fontFamily != null) s = s.copy(fontFamily = fontFamily)
        if (textAlign != null) s = s.copy(textAlign = textAlign)
        s.copy(fontSize = textSize.sp)
    }

    // This is the key: selection is observable via TextFieldValue.
    var value by remember(annotated) {
        mutableStateOf(
            TextFieldValue(
                annotatedString = annotated,
                selection = TextRange(0, 0),
            )
        )
    }

    // Keep the latest TextLayoutResult so we can anchor the menu near selection.
    var lastLayout by remember { mutableStateOf<TextLayoutResult?>(null) }

    // Our custom menu state.
    var menuExpanded by remember { mutableStateOf(false) }
    var menuAnchorPx by remember { mutableStateOf(Offset.Zero) }

    fun selectedTextOrNull(): String? {
        val sel = value.selection
        if (sel.collapsed) return null

        val start = sel.min.coerceIn(0, value.text.length)
        val end = sel.max.coerceIn(0, value.text.length)
        if (start >= end) return null

        return value.text.substring(start, end)
    }

    fun clearSelection() {
        // Collapse selection to remove highlight (similar to actionMode.finish()).
        value = value.copy(selection = TextRange(value.selection.end))
    }

    // Open/close our menu based on selection changes (NOT long-press).
    LaunchedEffect(Unit) {
        snapshotFlow { value.selection }
            .collectLatest { sel ->
                val layout = lastLayout
                if (layout == null) {
                    menuExpanded = false
                    return@collectLatest
                }

                if (sel.collapsed) {
                    menuExpanded = false
                    return@collectLatest
                }

                val safeLen = value.text.length
                if (safeLen == 0) {
                    menuExpanded = false
                    return@collectLatest
                }

                // Anchor near the end of the selection.
                val index = (sel.max - 1).coerceIn(0, safeLen - 1)

                val box = layout.getBoundingBox(index)

                // Convert text-layout coords to parent coords.
                // - Add padding (because we apply padding via modifier-like offset below).
                // - Subtract scroll (because content moves up when scrolling down).
                menuAnchorPx = Offset(
                    x = box.left + contentPaddingPx,
                    y = box.bottom + contentPaddingPx - scrollState.value.toFloat(),
                )

                menuExpanded = true
            }
    }

    ProvideNoSystemTextContextMenu {
        Box(modifier = Modifier.fillMaxSize()) {

            // NOTE: We apply padding by shifting the anchor and by drawing content with an offset-like approach:
            // Easiest: keep BasicTextField at (0,0), and just add padding using its modifier.
            BasicTextField(
                value = value,
                onValueChange = { newValue ->
                    // Selection changes arrive here.
                    value = newValue
                },
                readOnly = true,
                textStyle = textStyle.copy(color = Color.Black),
                modifier = Modifier
                    .fillMaxSize()
                    .mouseVerticalScrollbar(scrollState)
                    .verticalScroll(scrollState)
                    .then(Modifier) // keep chain readable
                    .run {
                        // Apply padding like your old Text:
                        // (We keep it here and also compensate the anchor with contentPaddingPx.)
                        Modifier.padding(contentPadding)
                    },
                onTextLayout = { layout ->
                    lastLayout = layout
                    onTextLayout(layout)
                },
                decorationBox = { inner ->
                    // Render like plain Text.
                    inner()
                }
            )

            val menuOffset = with(density) {
                DpOffset(menuAnchorPx.x.toDp(), menuAnchorPx.y.toDp())
            }

            DropdownMenu(
                expanded = menuExpanded && selectedTextOrNull() != null,
                onDismissRequest = {
                    // Close menu, keep selection (or clear it if you prefer).
                    menuExpanded = false
                },
                offset = menuOffset,
            ) {
                DropdownMenuItem(
                    text = { Text("העתקה") },
                    onClick = {
                        val selected = selectedTextOrNull().orEmpty()
                        onCopySelected(selected)
                        menuExpanded = false
                        clearSelection()
                    }
                )

                DropdownMenuItem(
                    text = { Text("חיפוש") },
                    onClick = {
                        val selected = selectedTextOrNull().orEmpty()
                        onSearchSelected(selected)
                        menuExpanded = false
                        clearSelection()
                    }
                )
            }
        }
    }
}


@Composable
private fun ProvideNoSystemTextContextMenu(content: @Composable () -> Unit) {
    // No-op provider that prevents Compose/Android default text context menu from showing.
    val noOpProvider = remember {
        object : TextContextMenuProvider {
            override suspend fun showTextContextMenu(dataProvider: TextContextMenuDataProvider) {
                // Intentionally no-op.
            }
        }
    }

    CompositionLocalProvider(
        LocalTextContextMenuToolbarProvider provides noOpProvider,
        LocalTextContextMenuDropdownProvider provides noOpProvider,
    ) {
        content()
    }
}
